
/**
 * @class TASPositivePayViewChecksSearch
 * @classdesc Class definition for controlling the properties that are set in a search feature.
 * 
 * @property {STRING} _accountID - The account id to filter by.
 * @property {STRING} _payee - The payee to filter by.
 * @property {STRING} _status - The status to filter by.
 * @property {STRING} _checkNumberFrom - The "check number from" to filter by.
 * @property {STRING} _checkNumberTo - The "check number to" to filter by.
 * @property {BCMSDate} _issuedDateFrom - The "issued date from" to filter by.
 * @property {BCMSDate} _minimumAmount - The minimum amount to filter by.
 * @property {STRING} _maximumAmount - The maximum amount to filter by.
 */
export default class TASPositivePayViewChecksSearch {
    _accountID;
    _payee;
    _status;
    _checkNumberFrom;
    _checkNumberTo;
    _issuedDateFrom;
    _issuedDateTo;
    _minimumAmount;
    _maximumAmount;

    constructor(record) {
        this.accountID = record.accountID;
        this.payee = record.payee;
        this.status = record.status;
        this.checkNumberFrom = record.checkNumberFrom;
        this.checkNumberTo = record.checkNumberTo;
        this.issuedDateFrom = record.issuedDateFrom;
        this.issuedDateTo = record.issuedDateTo;
        this.minimumAmount = record.minimumAmount;
        this.maximumAmount = record.maximumAmount;
    }

    /**
     * @name accountID
     * @instance
     * @property set {STRING} - sets the company id
     * @memberof TASPositivePayViewChecksSearch
     */
    set accountID(value) { this._accountID = value; }

    /**
     * @name payee
     * @instance
     * @property set {STRING} - sets the payee
     * @memberof TASPositivePayViewChecksSearch
     */
    set payee(value) { this._payee = value; }

    /**
     * @name status
     * @instance
     * @property set {STRING} - sets the status
     * @memberof TASPositivePayViewChecksSearch
     */
    set status(value) { this._status = value; }

    /**
     * @name checkNumberFrom
     * @instance
     * @property set {STRING} - sets the checkNumberFrom
     * @memberof TASPositivePayViewChecksSearch
     */
    set checkNumberFrom(value) { this._checkNumberFrom = value; }

    /**
     * @name checkNumberTo
     * @instance
     * @property set {STRING} - sets the checkNumberTo
     * @memberof TASPositivePayViewChecksSearch
     */
    set checkNumberTo(value) { this._checkNumberTo = value; }

    /**
     * @name issuedDateFrom
     * @instance
     * @property set {STRING} - sets the issuedDateFrom
     * @memberof TASPositivePayViewChecksSearch
     */
    set issuedDateFrom(value) { this._issuedDateFrom = value; }

    /**
     * @name issuedDateFrom
     * @instance
     * @property set {STRING} - sets the issuedDateTo
     * @memberof TASPositivePayViewChecksSearch
     */
    set issuedDateTo(value) { this._issuedDateTo = value; }

    /**
     * @name minimumAmount
     * @instance
     * @property set {STRING} - sets the minimumAmount
     * @memberof TASPositivePayViewChecksSearch
     */
    set minimumAmount(value) { this._minimumAmount = value; }

    /**
     * @name maximumAmount
     * @instance
     * @property set {STRING} - sets the maximumAmount
     * @memberof TASPositivePayViewChecksSearch
     */
    set maximumAmount(value) { this._maximumAmount = value; }

    /**
     * @name accountID
     * @instance
     * @property get {STRING} - returns the accountID
     * @memberof TransactionSearch
     */
    get accountID() { return this._accountID; }

    /**
     * @name payee
     * @instance
     * @property get {STRING} - returns the payee
     * @memberof TransactionSearch
     */
    get payee() { return this._payee; }

    /**
     * @name status
     * @instance
     * @property get {STRING} - returns the status
     * @memberof TransactionSearch
     */
    get status() { return this._status; }

    /**
     * @name checkNumberFrom
     * @instance
     * @property get {STRING} - returns the checkNumberFrom
     * @memberof TransactionSearch
     */
    get checkNumberFrom() { return this._checkNumberFrom; }

    /**
     * @name checkNumberTo
     * @instance
     * @property get {STRING} - returns the checkNumberTo
     * @memberof TransactionSearch
     */
    get checkNumberTo() { return this._checkNumberTo; }

    /**
     * @name issuedDateFrom
     * @instance
     * @property get {STRING} - returns the issuedDateFrom
     * @memberof TransactionSearch
     */
    get issuedDateFrom() { return this._issuedDateFrom; }

    /**
     * @name issuedDateTo
     * @instance
     * @property get {STRING} - returns the issuedDateTo
     * @memberof TransactionSearch
     */
    get issuedDateTo() { return this._issuedDateTo; }

    /**
     * @name minimumAmount
     * @instance
     * @property get {STRING} - returns the issuedDateTo
     * @memberof TransactionSearch
     */
    get minimumAmount() { return this._minimumAmount; }

    /**
     * @name maximumAmount
     * @instance
     * @property get {STRING} - returns the maximumAmount
     * @memberof TransactionSearch
     */
    get maximumAmount() { return this._maximumAmount; }


    /**
     * @name filters
     * @instance
     * @property get {OBJECT} - returns the set filter properties
     * @memberof TASPositivePayViewChecksSearch
     */
    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    /*get filters() {
        return Object.getOwnPropertyNames(this).reduce((result, name) => {
            const key = name.slice(1);
            const value = this[key];

            if(value !== undefined && value !== "" && (key !== "ranges" && key !== "types")){
                result[key] = value;
            }

            return result;
        }, {});
    }*/
    
    /**
     * @name record
     * @instance
     * @property get {OBJECT} - returns the set values
     * @memberof TASPositivePayViewChecksSearch
     */
    get record() {
        return {
            accountID: this.accountID,
            payee: this.payee,
            status: this.status,
            checkNumberFrom: this.checkNumberFrom,
            checkNumberTo: this.checkNumberTo,
            issuedDateFrom: this.issuedDateFrom,
            minimumAmount: this.minimumAmount,
            maximumAmount: this.maximumAmount,
        };
    }

    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    /*checkValue(transaction, key) {
        switch(key) {
            case "date":
                return transaction.date.isAfter(BCMSDate.init().offset(this.date));
    
            case "description":
                return transaction.description.toLowerCase().indexOf(this.description.toLowerCase()) > -1;
    
            case "type":
                return this.type === "all" ? true : transaction.filterType === this.type;
    
            case "amount":
                const amount = parseFloat(transaction.amount.amount);
                return parseFloat(this.amount.amount) === (amount < 0 ? -amount : amount);
    
            case "check":
                const desc = transaction.description;
                return desc.toLowerCase().indexOf("check") > -1 && desc.indexOf(this.check) > -1;
    
            default:
                return true;
        }
    }*/

    // TO DO: This code is from class "TransactionSearch" and similar support may be needed
    // once we have data to filter.
    /*filter() {
        return Transaction.initByAccount(this.accountID)
            .filter(transaction => {
                return Object.keys(this.filters).reduce((result, key) => {
                    if(!this.checkValue(transaction, key)) {
                        result = false;
                    }

                    return result;
                }, true);
            }, []);
    }*/

    reset() {
        const vals = { 
            accountID: undefined,
            payee: undefined,
            status: "all",
            checkNumberFrom: undefined,
            checkNumberTo: undefined,
            issuedDateFrom: undefined,
            issuedDateTo: undefined,
            minimumAmount: undefined,
            maximumAmount: undefined,
        };
        return TASPositivePayViewChecksSearch.init(vals);
    }
    
    static init(record) {
        const vals = { 
            accountID: undefined,
            payee: undefined,
            status: "all",
            checkNumberFrom: undefined,
            checkNumberTo: undefined,
            issuedDateFrom: undefined,
            issuedDateTo: undefined,
            minimumAmount: undefined,
            maximumAmount: undefined,
            ...record ?? {} 
        }
        return new TASPositivePayViewChecksSearch(vals);
    }
}